import PropTypes from 'prop-types';
import { useMemo } from 'react';

import useTranslations from '@/i18n/useTranslations';
import XIcon from '@/shared-components/icons/components/public/XIcon';

import { getStreakLabel, useColorMixLeaderboard } from './leaderboard';

const LeaderboardHeader = ({ t }) => (
  <div className="color-mix__leaderboard-header" role="row">
    <span role="columnheader" className="color-mix__leaderboard-col color-mix__leaderboard-col--rank">#</span>
    <span role="columnheader" className="color-mix__leaderboard-col color-mix__leaderboard-col--name">{t('columns.user')}</span>
    <span role="columnheader" className="color-mix__leaderboard-col color-mix__leaderboard-col--streak">{t('columns.best')}</span>
    <span role="columnheader" className="color-mix__leaderboard-col color-mix__leaderboard-col--points">{t('columns.points')}</span>
    <span role="columnheader" className="color-mix__leaderboard-col color-mix__leaderboard-col--pours">{t('columns.pours')}</span>
  </div>
);

const LeaderboardRow = ({ entry, highlight }) => (
  <div
    className={`color-mix__leaderboard-row${highlight ? ' is-highlighted' : ''}`}
    role="row"
  >
    <span role="cell" className="color-mix__leaderboard-col color-mix__leaderboard-col--rank">{entry.displayRank ?? entry.rank}</span>
    <span role="cell" className="color-mix__leaderboard-col color-mix__leaderboard-col--name">{entry.displayName}</span>
    <span role="cell" className="color-mix__leaderboard-col color-mix__leaderboard-col--streak">{entry.bestStreak ?? entry.streak}</span>
    <span role="cell" className="color-mix__leaderboard-col color-mix__leaderboard-col--points">{entry.totalPoints}</span>
    <span role="cell" className="color-mix__leaderboard-col color-mix__leaderboard-col--pours">{entry.totalPours}</span>
  </div>
);

LeaderboardRow.propTypes = {
  entry: PropTypes.shape({
    id: PropTypes.string,
    rank: PropTypes.number,
    displayRank: PropTypes.number,
    displayName: PropTypes.string,
    bestStreak: PropTypes.number,
    currentStreak: PropTypes.number,
    streak: PropTypes.number,
    totalPoints: PropTypes.number,
    totalPours: PropTypes.number,
  }).isRequired,
  highlight: PropTypes.bool,
};

LeaderboardRow.defaultProps = {
  highlight: false,
};

const UserPlacement = ({ userRow, neighbors, t }) => {
  const rows = useMemo(() => {
    if (!userRow) {
      return [];
    }

    const combined = [];
    const neighborSet = new Map();

    neighbors.forEach((entry) => {
      const key = entry.id ?? entry.rank ?? entry.displayRank;
      if (!neighborSet.has(key)) {
        neighborSet.set(key, entry);
        combined.push(entry);
      }
    });

    const userKey = userRow.id ?? userRow.rank ?? userRow.displayRank;
    if (!neighborSet.has(userKey)) {
      combined.push(userRow);
    }

    return combined
      .sort((a, b) => {
        const aRank = a.rank ?? a.displayRank ?? Number.POSITIVE_INFINITY;
        const bRank = b.rank ?? b.displayRank ?? Number.POSITIVE_INFINITY;
        return aRank - bRank;
      })
      .slice(0, 5);
  }, [neighbors, userRow]);

  if (!userRow) {
    return null;
  }

  return (
    <div className="color-mix__leaderboard-user" aria-live="polite">
      <div className="color-mix__leaderboard-user-header">
        <h3>{t('your_ranking')}</h3>
        <span className="color-mix__leaderboard-user-threshold">{t(`streak_labels.${getStreakLabel(userRow.currentStreak ?? userRow.streak)}`)}</span>
      </div>
      <div className="color-mix__leaderboard-table" role="table" aria-label={t('your_ranking_aria')}>
        <LeaderboardHeader t={t} />
        <div role="rowgroup">
          {rows.map((entry) => (
            <LeaderboardRow
              key={`${entry.displayRank ?? entry.rank}-${entry.id || entry.displayName}`}
              entry={entry}
              highlight={
                Boolean(userRow)
                && (
                  (entry.id && userRow.id && entry.id === userRow.id)
                  || (entry.rank != null && userRow.rank != null && entry.rank === userRow.rank)
                  || (entry.displayRank != null && userRow.displayRank != null && entry.displayRank === userRow.displayRank)
                )
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
};

UserPlacement.propTypes = {
  userRow: PropTypes.shape({
    id: PropTypes.string,
    rank: PropTypes.number,
    displayRank: PropTypes.number,
    displayName: PropTypes.string,
    bestStreak: PropTypes.number,
    currentStreak: PropTypes.number,
    streak: PropTypes.number,
    totalPoints: PropTypes.number,
    totalPours: PropTypes.number,
  }),
  neighbors: PropTypes.arrayOf(
    PropTypes.shape({
      rank: PropTypes.number,
      displayRank: PropTypes.number,
      displayName: PropTypes.string,
      bestStreak: PropTypes.number,
      currentStreak: PropTypes.number,
      streak: PropTypes.number,
      totalPoints: PropTypes.number,
      totalPours: PropTypes.number,
    }),
  ),
  t: PropTypes.func.isRequired,
};

UserPlacement.defaultProps = {
  userRow: null,
  neighbors: [],
};

export default function LeaderboardPanel(props) {
  const t = useTranslations('common.newspaper.art_games.color_mix.leaderboard');
  const hookData = useColorMixLeaderboard();
  const {
    entries,
    userRow,
    neighborRows,
    isLoading,
    error,
  } = {
    entries: props.entries ?? hookData.entries,
    userRow: props.userRow ?? hookData.userRow,
    neighborRows: props.neighborRows ?? hookData.neighborRows,
    isLoading: props.isLoading ?? hookData.isLoading,
    error: props.error ?? hookData.error,
  };

  return (
    <section className="color-mix__leaderboard" aria-label={t('title')}>
      <div className="generic-popup__header color-mix__leaderboard-heading">
        <h2 className="generic-popup__header-text">{t('title')}</h2>
        {props.onClose ? (
          <XIcon className="generic-popup__close" onClick={props.onClose} aria-label={t('close_aria')} />
        ) : null}
      </div>
      {error ? (
        <p role="alert" className="color-mix__leaderboard-error">{error.message}</p>
      ) : null}
      <div className="color-mix__leaderboard-table" role="table" aria-label={t('table_aria')}>
        <LeaderboardHeader t={t} />
        <div role="rowgroup">
          {entries.length === 0 && !isLoading ? (
            <p className="color-mix__leaderboard-empty">{t('empty')}</p>
          ) : (
            entries.map((entry) => (
              <LeaderboardRow
                key={`${entry.displayRank ?? entry.rank}-${entry.id || entry.displayName}`}
                entry={entry}
                highlight={
                  Boolean(userRow)
                  && (
                    (entry.id && userRow.id && entry.id === userRow.id)
                    || (entry.rank != null && userRow.rank != null && entry.rank === userRow.rank)
                    || (entry.displayRank != null && userRow.displayRank != null && entry.displayRank === userRow.displayRank)
                  )
                }
              />
            ))
          )}
        </div>
      </div>
      {userRow ? <UserPlacement userRow={userRow} neighbors={neighborRows} t={t} /> : null}
    </section>
  );
}

LeaderboardPanel.propTypes = {
  entries: PropTypes.arrayOf(PropTypes.object),
  userRow: PropTypes.shape({
    id: PropTypes.string,
    rank: PropTypes.number,
    displayRank: PropTypes.number,
    displayName: PropTypes.string,
    bestStreak: PropTypes.number,
    currentStreak: PropTypes.number,
    streak: PropTypes.number,
    totalPoints: PropTypes.number,
    totalPours: PropTypes.number,
  }),
  neighborRows: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  error: PropTypes.object,
  onClose: PropTypes.func,
};

LeaderboardPanel.defaultProps = {
  entries: null,
  userRow: null,
  neighborRows: [],
  isLoading: false,
  error: null,
  onClose: null,
};
